import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2, Palette } from "lucide-react";

const editTagSchema = z.object({
  name: z.string().min(1, "Tag name is required"),
  color: z.string().min(4, "Please select a color"),
});

interface Tag {
  id: string;
  name: string;
  color: string;
  category: string;
}

interface TagEditModalProps {
  tag: Tag;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const COLOR_PALETTE = [
  "#3b82f6", // Blue
  "#ef4444", // Red
  "#10b981", // Green
  "#f59e0b", // Yellow
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#06b6d4", // Cyan
  "#84cc16", // Lime
  "#f97316", // Orange
  "#6366f1", // Indigo
  "#14b8a6", // Teal
  "#eab308", // Amber
];

export const TagEditModal = ({ tag, open, onOpenChange }: TagEditModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedColor, setSelectedColor] = useState(tag.color);

  const form = useForm<z.infer<typeof editTagSchema>>({
    resolver: zodResolver(editTagSchema),
    defaultValues: {
      name: tag.name,
      color: tag.color,
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: async (values: z.infer<typeof editTagSchema>) => {
      const { data, error } = await supabase
        .from("tags")
        .update({
          name: values.name,
          color: values.color,
        })
        .eq("id", tag.id);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      onOpenChange(false);
      toast({ title: "Success", description: "Tag updated successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to update tag: " + error.message,
        variant: "destructive"
      });
    }
  });

  const onSubmit = (values: z.infer<typeof editTagSchema>) => {
    updateTagMutation.mutate(values);
  };

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    form.setValue("color", color);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Tag</DialogTitle>
          <DialogDescription>
            Update tag name and color for {tag.category} category
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tag Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter tag name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      <span className="text-sm text-muted-foreground">Select from palette:</span>
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {COLOR_PALETTE.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => handleColorSelect(color)}
                          className={`w-8 h-8 rounded-md border-2 transition-all ${
                            selectedColor === color 
                              ? 'border-foreground scale-110' 
                              : 'border-transparent hover:border-muted-foreground'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Or choose custom:</span>
                      <FormControl>
                        <Input 
                          type="color" 
                          className="w-16 h-8 p-1" 
                          value={selectedColor}
                          onChange={(e) => handleColorSelect(e.target.value)}
                        />
                      </FormControl>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Preview:</span>
                      <div 
                        className="px-3 py-1 rounded-md text-white text-sm font-medium"
                        style={{ backgroundColor: selectedColor }}
                      >
                        {form.watch("name") || tag.name}
                      </div>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateTagMutation.isPending}>
                {updateTagMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};